import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import SafeDrivingDailyGraphEu from '@di-shared/features/safe-driving/eu/report/SafeDrivingDailyGraph.eu';
import React, {Suspense, useState} from 'react';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {ErrorBoundary} from 'react-error-boundary';
import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApiEu} from '@di-shared/api/di';
import MonthlySafetyScoreChartEu from '@di-shared/features/safe-driving/eu/report/MonthlySafetyScoreChart.eu';
import SafeDrivingScoreDetailsEu from '@di-shared/features/safe-driving/eu/report/SafeDrivingScoreDetails.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import clsx from 'clsx';
import {checkExcludeBrand} from '@di-shared/utils/whitelist';
import {BrandType} from '@shared/cci/features/const/environment';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import type {ApiResponseMonthlyTrendResponse, ApiResponseTermsConsentStatus} from '@di-shared/api/di/__generated_eu';

export interface SafeDrivingReportEuProps {
  vin: string;
}

function SafeDrivingReportEu({vin}: SafeDrivingReportEuProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [{data: monthlyTrendData}, {data: termsData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiEu.safeDriving.monthlyTrend1), vin],
        queryFn: () => diApiEu.safeDriving.monthlyTrend1({vin}),
        select: ({data}: ApiResponseMonthlyTrendResponse) => ({
          ...data,
          lastUpdatedDate: parseDateWithFormat(data.lastUpdatedDate),
        }),
      },
      {
        queryKey: [getQueryKey(diApiEu.vehicle.getConsentedSelectiveTermsCode), vin, 'SAFETY_SCORE'],
        queryFn: () =>
          diApiEu.vehicle.getConsentedSelectiveTermsCode({
            vin,
            termscode: 'SAFETY_SCORE',
          }),
        select: ({data}: ApiResponseTermsConsentStatus) => {
          return {
            ...data,
            consentDate: parseDateWithFormat(data.consentDate),
          };
        },
      },
    ],
  });

  const monthlyScoreResponseList = monthlyTrendData.monthlyScoreResponseList || [];
  const [selectedMonth, setSelectedMonth] = useState<string>(monthlyScoreResponseList[monthlyScoreResponseList.length - 1]?.month);
  const safetyScoreDiff =
    monthlyScoreResponseList.length > 1
      ? monthlyScoreResponseList[monthlyScoreResponseList.length - 1].score -
        monthlyScoreResponseList[monthlyScoreResponseList.length - 2].score
      : null;

  return (
    <main
      id="content"
      className="content page_safe_driving"
    >
      <section className="section section_safe_driving_report">
        <div className="section_heading">
          <h3
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('ctn_dss_LN_your_monthly_driving_safety_score')}
            {safetyScoreDiff !== null && safetyScoreDiff > 0 && (
              <span className="badge badge_25px badge_filled_surface_container_lowest">
                <span
                  className="ic ic_score_up"
                  aria-hidden="true"
                />
                {t('ctn_dss_LN_points_$score', {score: safetyScoreDiff})}
              </span>
            )}
            {safetyScoreDiff != null && safetyScoreDiff < 0 && (
              <span className="badge badge_25px badge_filled_surface_container_lowest">
                <span
                  className="ic ic_score_down"
                  aria-hidden="true"
                />
                {t('ctn_dss_LN_points_$score', {score: -safetyScoreDiff})}
              </span>
            )}
          </h3>
          {monthlyTrendData.lastUpdatedDate && (
            <span className="section_heading_caption">
              {t('cts_dss_LN_last_updated')}&nbsp;
              {monthlyTrendData.lastUpdatedDate
                ? CldrDatetimeFormatter.format(monthlyTrendData.lastUpdatedDate, 'yMMMaHM')
                : termsData.consentDate
                  ? CldrDatetimeFormatter.format(termsData.consentDate, 'yMMMaHM')
                  : CldrDatetimeFormatter.format(today, 'yMMMaHM')}
            </span>
          )}
        </div>
        <MonthlySafetyScoreChartEu
          data={monthlyTrendData.monthlyScoreResponseList.map((monthlyTrendItem) => ({
            date: monthlyTrendItem.month,
            score: monthlyTrendItem.score,
          }))}
          selectedMonth={selectedMonth}
          onSelectedMonth={(month: string) => {
            setSelectedMonth(month);
          }}
        />
        <Suspense fallback={<SafeDrivingScoreDetailsEu.Loading />}>
          <SafeDrivingScoreDetailsEu
            vin={vin}
            selectedMonth={selectedMonth}
          />
        </Suspense>

        <div className="driving_banner type_square">
          <LinkTo
            to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
            className="driving_banner_box"
          >
            <span className="driving_banner_txtbox type_reverse">
              <strong className="driving_banner_title body_large_b">{t('msg_dss_LN_need_a_detailed_trip_report')}</strong>
              <span className="driving_banner_desc body_small_r">{t('ctn_dss_LN_my_trips')}</span>
            </span>
            <span className="driving_banner_imgbox ">
              <ImageSelector
                className="driving_banner_img"
                width="80"
                height="80"
                alt="driving_banner_img"
                kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
              />
            </span>
          </LinkTo>
        </div>

        <ErrorBoundary fallback={<SafeDrivingDailyGraphEu.Error />}>
          <Suspense fallback={<SafeDrivingDailyGraphEu.Loading />}>
            <SafeDrivingDailyGraphEu
              vin={vin}
              selectedMonth={selectedMonth}
            />
          </Suspense>
        </ErrorBoundary>

        {/* [2025-02-06:dooking] FIXME: 현재 스펙상에서 숨김 처리 필요 */}
        {checkExcludeBrand(BrandType.HYUNDAI, BrandType.KIA, BrandType.GENESIS) && (
          <div className="section_container">
            <div className="section_container_header">
              <h3 className="title_xsmall_b">{t('T200024')}</h3>
            </div>
            <p className="section_container_desc">{t('T200025')}</p>
            <button
              type="button"
              className="btn btn_large btn_filled btn_full"
            >
              {t('T200026')}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

function SafeDrivingReportEuError({vin}: SafeDrivingReportEuProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  return (
    <main
      id="content"
      className="content page_safe_driving"
    >
      <section className="section section_safe_driving_report">
        <div className="section_heading">
          <h3
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('ctn_dss_LN_your_monthly_driving_safety_score')}
          </h3>
          <span className="section_heading_caption">
            {t('cts_dss_LN_last_updated')}
            {CldrDatetimeFormatter.format(today, 'yMMMaHM')}
          </span>
        </div>
        <div className="section_container">
          <div className="statistics_graph_wrap">
            <div className="statistics_graph_area graph_full_type">
              <div className="bar_graph">
                {Array.from({length: 6}).map((_, index) => (
                  <div
                    className="bar_graph_item"
                    key={index}
                  >
                    <span
                      className="graph_bar"
                      style={{height: '0'}}
                    ></span>
                    <span className="graph_title">{CldrDatetimeFormatter.format(subDate(new Date(), {months: 5 - index}), 'yMMM')}</span>
                  </div>
                ))}

                <div
                  className="bar_graph_average"
                  style={{top: '55px'}}
                >
                  <span className="average_line"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SafeDrivingScoreDetailsEu.NoData />
        <div className="driving_banner type_square">
          <LinkTo
            to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
            className="driving_banner_box"
          >
            <span className="driving_banner_txtbox type_reverse">
              <strong className="driving_banner_title body_large_b">{t('msg_dss_LN_need_a_detailed_trip_report')}</strong>
              <span className="driving_banner_desc body_small_r">{t('ctn_dss_LN_my_trips')}</span>
            </span>
            <span className="driving_banner_imgbox ">
              <ImageSelector
                className="driving_banner_img"
                width="80"
                height="80"
                alt="driving_banner_img"
                kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
              />
            </span>
          </LinkTo>
        </div>
        <SafeDrivingDailyGraphEu.Error />
      </section>
    </main>
  );
}

function SafeDrivingReportEuLoading() {
  const {t} = useInferableTranslation();

  return (
    <main
      id="content"
      className="content page_safe_driving"
    >
      <section className="section section_safe_driving_report">
        <div className="section_heading">
          <h3
            className="section_heading_title skeleton"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('ctn_dss_LN_your_monthly_driving_safety_score')}
            <span className="badge badge_25px badge_filled_surface_container_lowest">
              <span
                className="ic ic_score_up"
                aria-hidden="true"
              />
              {t('ctn_dss_LN_points_$score', {score: '10000'})}
            </span>
          </h3>
          <span className="section_heading_caption skeleton">
            {t('cts_dss_LN_last_updated')}
            {'27-01-2023 19:43'}
          </span>
        </div>
        <div className="section_container skeleton">
          <div className="statistics_graph_wrap">
            <div className={clsx('statistics_graph_area graph_full_type')}>
              <div className="bar_graph">
                <div className="bar_graph_average">
                  <span className="average_line"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SafeDrivingScoreDetailsEu.Loading />

        <div className="driving_banner type_square skeleton">
          <a
            href="#"
            className="driving_banner_box"
          >
            <span className="driving_banner_txtbox type_reverse">
              <strong className="driving_banner_title body_large_b">{t('msg_dss_LN_need_a_detailed_trip_report')}</strong>
              <span className="driving_banner_desc body_small_r">{t('ctn_dss_LN_my_trips')}</span>
            </span>
            <span className="driving_banner_imgbox ">
              <ImageSelector
                className="driving_banner_img"
                width="80"
                height="80"
                alt="driving_banner_img"
                kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_my_trips.png')}
              />
            </span>
          </a>
        </div>

        <SafeDrivingDailyGraphEu.Loading />
      </section>
    </main>
  );
}

SafeDrivingReportEu.Error = SafeDrivingReportEuError;
SafeDrivingReportEu.Loading = SafeDrivingReportEuLoading;

export default SafeDrivingReportEu;
